import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  FileText,
  MessageSquare,
  Activity,
  PenSquare,
  XCircle,
  Pencil,
  Flag,
  RefreshCw,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import EditCustomerModal from './case_workspace/EditCustomerModal';
import EditEventModal from './case_workspace/EditEventModal';
import ConciergePlaybook from './case_workspace/ConciergePlaybook';
import CaseWorkspaceTopStripe from './case_workspace/CaseWorkspaceTopStripe';
import JourneyStrip from './case_workspace/JourneyStrip';
import CloseCaseModal from './case_workspace/CloseCaseModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREEN = '#0F5132';
const PURPLE = '#5B47E8';

// ── small helpers ────────────────────────────────────────────────────────

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Compact 6-char hash so admins can refer to a case verbally
 *  ("CCS-3F2A91"). The hash is derived from the event_id and is
 *  reproducible. No collisions matter at this scale. */
const shortCaseId = (eventId) => {
  if (!eventId) return '------';
  const flat = String(eventId).replace(/-/g, '');
  return flat.slice(0, 6).toUpperCase();
};

const friendlyDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (_) {
    return null;
  }
};

const friendlyDay = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (_) {
    return null;
  }
};

const relativeTime = (iso) => {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const seconds = Math.floor((Date.now() - then) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
    return `${Math.floor(seconds / 86400)} d ago`;
  } catch (_) {
    return null;
  }
};

// ── status presentation ──────────────────────────────────────────────────

const STATUS_LABELS = {
  new: 'New',
  claimed: 'Claimed',
  reviewing: 'Reviewing',
  researching: 'Researching',
  waiting_customer: 'Waiting on customer',
  waiting_vendor: 'Waiting on vendor',
  recommendations_sent: 'Recommendations sent',
  followup_needed: 'Follow-up needed',
  closed: 'Closed',
};

// Mirrors backend SETTABLE_STATUSES: excludes "new" (intake-only) and
// "closed" (close button territory — see B-EXP-7). Ordered to match the
// natural progression of a Concierge workflow.
const SETTABLE_STATUS_OPTIONS = [
  'claimed',
  'reviewing',
  'researching',
  'waiting_customer',
  'waiting_vendor',
  'recommendations_sent',
  'followup_needed',
];

const STATUS_CHIP_TONES = {
  new: 'bg-gray-100 text-gray-700 border-gray-200',
  claimed: 'bg-violet-100 text-violet-700 border-violet-200',
  reviewing: 'bg-violet-100 text-violet-700 border-violet-200',
  researching: 'bg-violet-100 text-violet-700 border-violet-200',
  waiting_customer: 'bg-amber-50 text-amber-800 border-amber-200',
  waiting_vendor: 'bg-amber-50 text-amber-800 border-amber-200',
  recommendations_sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  followup_needed: 'bg-rose-50 text-rose-700 border-rose-200',
  closed: 'bg-stone-100 text-stone-600 border-stone-200',
};

const NEXT_ACTION_COPY = {
  new: 'Claim from the queue to start working this case.',
  claimed: 'Review the customer\u2019s request and decide on next steps.',
  reviewing: 'Capture initial thoughts and any open questions for the customer.',
  researching: 'Continue research \u2014 add notes as you find what fits.',
  waiting_customer: 'Awaiting customer reply by email. Check back tomorrow if no response.',
  waiting_vendor: 'Awaiting vendor reply. Log the outreach in the notes if helpful.',
  recommendations_sent: 'Recommendations are with the customer. Watch for their reply.',
  followup_needed: 'Customer is waiting on something \u2014 don\u2019t let this one drift.',
  closed: 'This case is closed. Reopen if new activity comes in.',
};

// ── small presentational components ──────────────────────────────────────

const EmDash = () => <span className="text-gray-400">{'\u2014'}</span>;

const FieldRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3 py-2" data-testid={testId}>
    {Icon ? <Icon className="w-4 h-4 text-[#8B7B5A] mt-0.5 shrink-0" aria-hidden /> : null}
    <div className="min-w-0 flex-1">
      <p className="text-[11px] uppercase tracking-[0.1em] text-[#8B7B5A] leading-none font-medium">
        {label}
      </p>
      <div className="text-[14px] text-gray-900 mt-1 break-words leading-snug font-medium">
        {value || <EmDash />}
      </div>
    </div>
  </div>
);

const CardShell = ({ title, action, children, testId }) => (
  <Card className="rounded-2xl border-gray-100" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs uppercase tracking-wide text-gray-500 font-medium">{title}</h2>
        {action ? action : null}
      </div>
      {children}
    </CardContent>
  </Card>
);

// RailSection — contained rail panel (Celey warm card treatment).
// Dark-green section heading, white surface, subtle warm border.
const RailSection = ({ title, action, children, testId, className = '' }) => (
  <section
    data-testid={testId}
    className={`bg-white rounded-2xl border border-[#EFE7D4] shadow-[0_1px_2px_rgba(15,23,42,0.03)] p-5 ${className}`}
  >
    <div className="flex items-center justify-between mb-4">
      <h2
        className="text-[12px] uppercase tracking-[0.14em] font-semibold"
        style={{ color: GREEN }}
      >
        {title}
      </h2>
      {action ? action : null}
    </div>
    {children}
  </section>
);

const SoonButton = ({ icon: Icon, label, testId }) => (
  <Button
    variant="outline"
    disabled
    aria-disabled="true"
    title="Wired in the next step"
    data-testid={testId}
    className="opacity-70 cursor-not-allowed gap-2"
  >
    {Icon ? <Icon className="w-4 h-4" aria-hidden /> : null}
    {label}
    <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0">Soon</Badge>
  </Button>
);

// ── Case History (B-EXP-5) ──────────────────────────────────────────────
// Icon + tint for each known audit operation. Falls back to Activity.
const HISTORY_ICONS = {
  case_submitted:  { icon: FileText,   bg: '#EBE6FF', fg: '#5B47E8' },
  claim:           { icon: Users,      bg: '#FDE4DC', fg: '#B4321C' },
  release:         { icon: Users,      bg: '#FBEAD5', fg: '#8B5A0F' },
  reassign:        { icon: Users,      bg: '#EBE6FF', fg: '#5B47E8' },
  status_changed:  { icon: Activity,   bg: '#DFF1E1', fg: '#146C43' },
  add_note:        { icon: MessageSquare, bg: '#FDEFC5', fg: '#8B5A0F' },
  'customer.edit': { icon: PenSquare,  bg: '#EBE6FF', fg: '#5B47E8' },
  'event.edit':    { icon: PenSquare,  bg: '#EBE6FF', fg: '#5B47E8' },
  reach_out_vendor:{ icon: Mail,       bg: '#FCE0EE', fg: '#B4297A' },
  complete:        { icon: Activity,   bg: '#DFF1E1', fg: '#146C43' },
  case_closed:     { icon: XCircle,    bg: '#FDE4DC', fg: '#B4321C' },
  case_reopened:   { icon: Activity,   bg: '#DFF1E1', fg: '#146C43' },
  'brief.published':          { icon: FileText,      bg: '#EBE6FF', fg: '#5B47E8' },
  'brief.customer_responded': { icon: MessageSquare, bg: '#E2F0E5', fg: '#0F5132' },
};

const HistoryRow = ({ entry }) => {
  const tint = HISTORY_ICONS[entry.operation] || { icon: Activity, bg: '#F5EEDD', fg: '#8B7B5A' };
  const Icon = tint.icon;
  // B-EXP-6.24 — render the real staff display name for every action
  // so the shared operational workspace is unambiguous across
  // multiple concierge operators. Customer / system rows retain
  // their explicit labels; deleted users fall back to "Concierge
  // admin" (unresolved id).
  let actorLabel;
  if (entry.actor_kind === 'customer') actorLabel = 'Customer';
  else if (entry.actor_kind === 'system') actorLabel = 'System';
  else if (entry.actor_display_name) actorLabel = entry.actor_display_name;
  else if (entry.actor_id) actorLabel = 'Concierge admin';
  else actorLabel = 'System';
  const created = entry.created_at;
  return (
    <li
      className="flex items-start gap-3"
      data-testid={`case-history-row-${entry.id}`}
      data-operation={entry.operation}
    >
      <span
        className="flex-none mt-0.5 w-8 h-8 rounded-full inline-flex items-center justify-center"
        style={{ backgroundColor: tint.bg }}
        aria-hidden
      >
        <Icon className="w-4 h-4" style={{ color: tint.fg }} />
      </span>
      <div className="min-w-0 flex-1">
        <p
          className="text-[13.5px] font-medium text-gray-900 leading-snug"
          data-testid={`case-history-row-label-${entry.id}`}
        >
          {entry.label}
        </p>
        <p className="text-[11px] text-gray-500 mt-1 leading-snug">
          <span data-testid={`case-history-row-actor-${entry.id}`}>{actorLabel}</span>
          <span aria-hidden> · </span>
          <span
            title={created || ''}
            data-testid={`case-history-row-time-${entry.id}`}
          >
            {relativeTime(created)}
          </span>
        </p>
      </div>
    </li>
  );
};

const SkeletonBlock = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />
);

const NOTE_MAX = 4000;

// ── Customer responses helpers (B-EXP-6.19 Defect 4) ────────────────────
// Kept lightweight — the "current decision" summary below does not use
// per-row tinted icons; a compact bucket header + colored count chip is
// enough for staff scanning.

const _shortAgo = (iso) => {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch (_) {
    return '';
  }
};

// ── CustomerResponsesSummary (B-EXP-6.19 Defect 4) ───────────────────────
// Renders the "current decision" summary rather than a chronological
// feed. Rules:
//   * One row per recommendation (or one "General feedback" row per
//     comment). We collapse across responder/revision by
//     (recommendation_id ?? 'general:'+id) and take the LATEST
//     updated_at as the current state.
//   * Grouped into three buckets: Interested / Not for me / General
//     feedback. Empty buckets hide.
//   * The row still shows the brief_revision suffix (v1, v2, …) so
//     older-brief opinions are visibly honest.
//   * `responses` array from the API is already server-side upserted
//     within a revision (see concierge_brief_responses.upsert_response),
//     so the frontend collapse here only kicks in for cross-revision
//     changes.

const RESPONSE_BUCKET_META = {
  interested: {
    key: 'interested',
    title: 'Interested',
    testId: 'case-responses-bucket-interested',
    dotClass: 'bg-emerald-100 text-emerald-700',
  },
  not_for_me: {
    key: 'not_for_me',
    title: 'Not for me',
    testId: 'case-responses-bucket-not-for-me',
    dotClass: 'bg-rose-100 text-rose-700',
  },
  general_feedback: {
    key: 'general_feedback',
    title: 'General feedback',
    testId: 'case-responses-bucket-general',
    dotClass: 'bg-violet-100 text-violet-700',
  },
};

const _summaryKey = (r) => {
  if (r.recommendation_id) return `rec:${r.recommendation_id}`;
  return `general:${r.id}`;
};

const _byUpdatedAtDesc = (a, b) => {
  const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
  const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
  return tb - ta;
};

const CustomerResponsesSummary = ({ responses }) => {
  // Collapse to latest response per (recommendation | general-row).
  const collapsed = useMemo(() => {
    const map = new Map();
    for (const r of responses) {
      const key = _summaryKey(r);
      const existing = map.get(key);
      if (!existing) { map.set(key, r); continue; }
      const ta = existing.updated_at ? new Date(existing.updated_at).getTime() : 0;
      const tb = r.updated_at ? new Date(r.updated_at).getTime() : 0;
      if (tb > ta) map.set(key, r);
    }
    return Array.from(map.values());
  }, [responses]);

  const buckets = useMemo(() => {
    const out = { interested: [], not_for_me: [], general_feedback: [] };
    for (const r of collapsed) {
      const t = r.response_type in out ? r.response_type : 'general_feedback';
      out[t].push(r);
    }
    for (const k of Object.keys(out)) out[k].sort(_byUpdatedAtDesc);
    return out;
  }, [collapsed]);

  const orderedBucketKeys = ['interested', 'not_for_me', 'general_feedback'];

  return (
    <div className="space-y-4" data-testid="case-responses-summary">
      {orderedBucketKeys.map((bk) => {
        const meta = RESPONSE_BUCKET_META[bk];
        const rows = buckets[bk];
        if (!rows || rows.length === 0) return null;
        return (
          <div key={bk} data-testid={meta.testId}>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-[11px] uppercase tracking-[0.14em] font-semibold text-gray-700">
                {meta.title}
              </h3>
              <span
                className={`text-[10.5px] font-medium rounded-full px-1.5 py-0.5 ${meta.dotClass}`}
                data-testid={`${meta.testId}-count`}
              >
                {rows.length}
              </span>
            </div>
            <ul className="space-y-2.5" data-testid={`${meta.testId}-list`}>
              {rows.map((r) => (
                <li
                  key={r.id}
                  data-testid={`case-responses-summary-row-${r.id}`}
                  data-response-type={r.response_type}
                  data-recommendation-id={r.recommendation_id || ''}
                >
                  <p
                    className="text-[13px] text-gray-900 leading-snug"
                    data-testid={`case-responses-summary-row-name-${r.id}`}
                  >
                    {r.response_type === 'general_feedback'
                      ? <span className="italic text-gray-700">Feedback</span>
                      : (r.recommendation_name
                          ? <strong>{r.recommendation_name}</strong>
                          : <span className="italic text-gray-500">(recommendation removed)</span>)}
                  </p>
                  {r.comment ? (
                    <p
                      className="mt-0.5 text-[12.5px] leading-snug text-gray-700 italic"
                      data-testid={`case-responses-summary-row-comment-${r.id}`}
                    >
                      &ldquo;{r.comment}&rdquo;
                    </p>
                  ) : null}
                  <p
                    className="mt-0.5 text-[11px] text-gray-500"
                    data-testid={`case-responses-summary-row-time-${r.id}`}
                  >
                    {_shortAgo(r.updated_at)}
                    {r.brief_revision ? ` · v${r.brief_revision}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};


// Lazy read of the signed-in admin from localStorage. Used only to label
// notes as "You" vs "Concierge admin" — no PII rendered.
const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.id || u?.user_id || null;
  } catch (_) {
    return null;
  }
};

// ── page ─────────────────────────────────────────────────────────────────

export default function CaseWorkspacePage() {
  const { event_id: eventId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  const [editEventOpen, setEditEventOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeSaving, setCloseSaving] = useState(false);
  const [reopenSaving, setReopenSaving] = useState(false);
  const [publishStatus, setPublishStatus] = useState(null);
  const [responses, setResponses] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(true);
  const currentUserId = useMemo(() => getCurrentUserId(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/admin/concierge/workspace/${eventId}`,
        { headers: authHeaders() },
      );
      setState(res.data);
    } catch (e) {
      setError(e?.response?.status || 'unknown');
    } finally {
      setLoading(false);
    }
  }, [eventId]);  const loadNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/concierge/workspace/${eventId}/notes?limit=100`,
        { headers: authHeaders() },
      );
      setNotes(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      // best-effort — keep whatever is already in state
    } finally {
      setNotesLoading(false);
    }
  }, [eventId]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/concierge/workspace/${eventId}/history?limit=100`,
        { headers: authHeaders() },
      );
      const entries = res?.data?.entries;
      setHistory(Array.isArray(entries) ? entries : []);
    } catch (_) {
      // best-effort — keep whatever is already in state
    } finally {
      setHistoryLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadNotes(); }, [loadNotes]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Publish status — fresh on mount, refreshed after publish / republish
  // and (best-effort) right before opening the publish modal.
  const loadPublishStatus = useCallback(async () => {
    if (!eventId) return null;
    try {
      const res = await axios.get(
        `${API}/admin/concierge/workspace/${eventId}/brief-publish-status`,
        { headers: authHeaders() },
      );
      setPublishStatus(res.data || null);
      return res.data || null;
    } catch (_) {
      // best-effort — leaves the button in its safe default state
      return null;
    }
  }, [eventId]);
  useEffect(() => { loadPublishStatus(); }, [loadPublishStatus]);

  // Customer responses — mounted lazily; refreshed after publish and
  // whenever the audit trail is refreshed (a customer response writes
  // both a response row AND a workspace_audit entry).
  const loadResponses = useCallback(async () => {
    if (!eventId) return;
    setResponsesLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/concierge/workspace/${eventId}/customer-responses`,
        { headers: authHeaders() },
      );
      setResponses(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (_) {
      // best-effort — leaves the rail card in its empty-state
    } finally {
      setResponsesLoading(false);
    }
  }, [eventId]);
  useEffect(() => { loadResponses(); }, [loadResponses]);

  // POST /brief-publish → seals + returns share URL. Refresh publish
  // status so the button label reconciles ("Send" → "Published").
  const publishBrief = useCallback(async () => {
    const res = await axios.post(
      `${API}/admin/concierge/workspace/${eventId}/brief-publish`,
      {},
      { headers: authHeaders() },
    );
    // Refresh status + audit trail in the background.
    loadPublishStatus();
    loadHistory();
    return res.data;
  }, [eventId, loadPublishStatus, loadHistory]);

  // GET /brief-preview.pdf → arraybuffer → Blob → new tab (uses the
  // same "pre-open placeholder tab" pattern as View as Customer so
  // browsers don't classify the popup as user-unintended).
  const openPdfPreview = useCallback(async () => {
    if (!eventId) return;
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write(
        '<!DOCTYPE html><html><head><title>Preparing PDF preview…</title>' +
        '<style>body{font-family:system-ui;background:#FFF7EE;color:#6B6558;' +
        'display:flex;align-items:center;justify-content:center;height:100vh;' +
        'margin:0;font-size:14px}</style></head><body>Preparing PDF preview…</body></html>'
      );
    }
    try {
      const res = await axios.get(
        `${API}/admin/concierge/workspace/${eventId}/brief-preview.pdf`,
        { headers: authHeaders(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (previewWindow && !previewWindow.closed) {
        previewWindow.location.replace(url);
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (previewWindow && !previewWindow.closed) {
        try { previewWindow.close(); } catch (_) { /* noop */ }
      }
      throw err;
    }
  }, [eventId]);

  const openForm = () => { setFormOpen(true); setDraft(''); };
  const cancelForm = () => { setFormOpen(false); setDraft(''); };

  const saveNote = async () => {
    const text = draft.trim();
    if (!text || text.length > NOTE_MAX) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/concierge/workspace/${eventId}/notes`,
        { text },
        { headers: authHeaders() },
      );
      // Optimistic prepend + background reconcile.
      const newNote = res.data || { id: `tmp-${Date.now()}`, text, created_at: new Date().toISOString(), worker_id: currentUserId };
      setNotes((prev) => [newNote, ...prev]);
      setFormOpen(false);
      setDraft('');
      toast.success('Note added');
      // Background refetch to reconcile any concurrent writes.
      loadNotes();
      loadHistory();
    } catch (e) {
      toast.error("Couldn't save the note. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const patchCustomer = async (changes) => {
    try {
      const res = await axios.patch(
        `${API}/admin/concierge/workspace/${eventId}/customer`,
        changes,
        { headers: authHeaders() },
      );
      // Optimistic merge into local state; background reload reconciles.
      setState((prev) => prev ? { ...prev, customer: { ...(prev.customer || {}), ...(res.data || changes) } } : prev);
      setEditCustomerOpen(false);
      toast.success('Customer updated');
      load();
      loadHistory();
      loadPublishStatus(); // customer overrides feed the customer-facing brief
    } catch (e) {
      toast.error("Couldn't save changes. Try again.");
      throw e;
    }
  };

  const patchEvent = async (changes) => {
    try {
      const res = await axios.patch(
        `${API}/admin/concierge/workspace/${eventId}/event`,
        changes,
        { headers: authHeaders() },
      );
      setState((prev) => prev ? { ...prev, event: { ...(prev.event || {}), ...(res.data || changes) } } : prev);
      setEditEventOpen(false);
      toast.success('Event details updated');
      load();
      loadHistory();
      loadPublishStatus(); // event overrides feed the customer-facing brief
    } catch (e) {
      toast.error("Couldn't save changes. Try again.");
      throw e;
    }
  };

  // B-EXP-4 — Workflow Status change. Optimistic flip; revert on failure.
  // Reuses Phase B1 endpoint PATCH /api/admin/concierge/cases/{id}/status
  // which writes the workspace_audit row.
  const changeStatus = async (newStatus) => {
    if (!newStatus || statusSaving) return;
    if (!state || state.status === newStatus) return;
    const previousStatus = state.status;
    const previousUpdatedAt = state.status_updated_at;
    const nowIso = new Date().toISOString();
    setStatusSaving(true);
    setState((prev) => prev ? { ...prev, status: newStatus, status_updated_at: nowIso } : prev);
    try {
      await axios.patch(
        `${API}/admin/concierge/cases/${eventId}/status`,
        { status: newStatus },
        { headers: authHeaders() },
      );
      // Reconcile with server (audit row, real timestamp).
      load();
      loadHistory();
    } catch (e) {
      // Revert optimistic update.
      setState((prev) => prev ? { ...prev, status: previousStatus, status_updated_at: previousUpdatedAt } : prev);
      toast.error("Couldn't update status. Try again.");
    } finally {
      setStatusSaving(false);
    }
  };

  // ── Close / Reopen (B-EXP-6.11) ────────────────────────────────────────
  const submitClose = async ({ reason, resolution_note }) => {
    if (closeSaving) return;
    setCloseSaving(true);
    try {
      await axios.post(
        `${API}/admin/concierge/cases/${eventId}/close`,
        { reason, resolution_note },
        { headers: authHeaders() },
      );
      toast.success('Case closed.');
      setCloseModalOpen(false);
      load();
      loadHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't close the case. Try again.";
      toast.error(detail);
    } finally {
      setCloseSaving(false);
    }
  };

  const reopenCase = async () => {
    if (reopenSaving) return;
    setReopenSaving(true);
    try {
      await axios.post(
        `${API}/admin/concierge/cases/${eventId}/reopen`,
        {},
        { headers: authHeaders() },
      );
      toast.success('Case reopened.');
      load();
      loadHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't reopen the case. Try again.";
      toast.error(detail);
    } finally {
      setReopenSaving(false);
    }
  };

  // ── derived display ────────────────────────────────────────────────────
  const display = useMemo(() => {
    if (!state) return null;
    const ev = state.event || {};
    const cu = state.customer || {};
    const status = state.status || 'new';
    const isClosed = status === 'closed';
    return {
      eventId: state.event_id,
      title: (ev.event_type && ev.event_type !== 'Event') ? ev.event_type : 'Concierge case',
      caseId: `CCS-${shortCaseId(state.event_id)}`,
      submittedAt: friendlyDate(ev.created_at),
      status,
      isClosed,
      statusUpdatedRelative: relativeTime(state.status_updated_at),
      closedAtFriendly: friendlyDate(state.closed_at),
      closeReason: state.close_reason,
      // customer fields
      customer: {
        name: cu.name,
        email: cu.email,
        phone: cu.phone,
        preferredContact: cu.preferred_contact || null,
        contactNote: cu.contact_note || null,
      },
      // event fields
      eventSummary: {
        // B-EXP-6.21 — canonical event title (edited via EditEventModal).
        // Falls back to event_type so pre-6.21 cases still show something
        // sensible on the Case Workspace event card.
        title: ev.title || null,
        type: ev.event_type,
        dateFriendly: friendlyDay(ev.event_date),
        location: ev.location_label,
        // Convert numeric 0 to "0" so the truthy-fallback in FieldRow
        // (`value || <EmDash />`) doesn't render an em-dash for a
        // legitimate zero value.
        guests: ev.guest_count != null ? String(ev.guest_count) : null,
        budget: ev.budget != null ? String(ev.budget) : null,
        submitted: friendlyDate(ev.created_at),
      },
      requestSummary: ev.intake_description || state.intake_description,
      notesCount: state.notes_count || 0,
      // history viewer not wired yet
      historyCount: null,
    };
  }, [state]);

  // ── loading ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF7EE]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          <SkeletonBlock className="h-6 w-32" />
          <SkeletonBlock className="h-20 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SkeletonBlock className="h-40" />
            <SkeletonBlock className="h-40" />
            <SkeletonBlock className="h-40" />
            <SkeletonBlock className="h-40" />
          </div>
          <SkeletonBlock className="h-32" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SkeletonBlock className="h-48" />
            <SkeletonBlock className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  // ── error fallback ─────────────────────────────────────────────────────
  if (error || !display) {
    return (
      <div className="min-h-screen bg-[#FFF7EE] flex items-center justify-center p-6">
        <div
          className="max-w-md text-center space-y-4"
          data-testid="case-workspace-error"
        >
          <p className="text-lg font-medium text-gray-900">
            We couldn&apos;t load this case
          </p>
          <p className="text-sm text-gray-600">
            It may have been removed, or you may not have access to it.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/concierge')}
            data-testid="case-workspace-error-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden />
            Back to queue
          </Button>
        </div>
      </div>
    );
  }

  // ── normal render ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#FFF7EE] overflow-x-hidden flex flex-col" data-testid="case-workspace">
      {/* Top stripe */}
      <CaseWorkspaceTopStripe
        onBack={() => navigate('/admin/concierge')}
        eventId={display.eventId}
        status={display.status}
        statusSaving={statusSaving}
        onStatusChange={changeStatus}
        isClosed={display.isClosed}
        publishStatus={publishStatus}
        onPublish={publishBrief}
        onRefetchPublishStatus={loadPublishStatus}
        onOpenPdfPreview={openPdfPreview}
        assignedWorker={state?.assigned_worker}
      />

      {/* Journey strip — orientation directly under identity */}
      <JourneyStrip
        createdAt={state?.event?.created_at}
        claimedAt={state?.claimed_at}
        status={display.status}
        briefDraft={state?.event?.brief_draft}
        publishStatus={publishStatus}
        closedAt={state?.closed_at || state?.status_updated_at}
      />

      {/* 3-column body */}
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 py-6 sm:py-8 flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)_300px] gap-5 lg:gap-6">
          {/* Left rail — Customer / Event / Request */}
          <aside className="order-2 lg:order-1 space-y-4" data-testid="case-context-rail">
            {/* Customer */}
            <RailSection
              title="Customer"
              testId="case-customer-card"
              action={
                <button
                  type="button"
                  onClick={() => setEditCustomerOpen(true)}
                  className="inline-flex items-center gap-1 text-[12px] font-medium hover:opacity-80"
                  style={{ color: GREEN }}
                  data-testid="case-customer-edit-btn"
                >
                  <Pencil className="w-3.5 h-3.5" aria-hidden />
                  Edit
                </button>
              }
            >
              <FieldRow icon={Users} label="Name" value={display.customer.name} testId="case-customer-name" />
              <FieldRow icon={Mail} label="Email" value={display.customer.email} testId="case-customer-email" />
              <FieldRow icon={Phone} label="Phone" value={display.customer.phone} testId="case-customer-phone" />
              {display.customer.preferredContact ? (
                <FieldRow label="Preferred contact" value={display.customer.preferredContact} testId="case-customer-preferred-contact" />
              ) : null}
              {display.customer.contactNote ? (
                <FieldRow label="Contact note" value={display.customer.contactNote} testId="case-customer-contact-note" />
              ) : null}
            </RailSection>

            {/* Event Details */}
            <RailSection
              title="Event Details"
              testId="case-event-card"
              action={
                <button
                  type="button"
                  onClick={() => setEditEventOpen(true)}
                  className="inline-flex items-center gap-1 text-[12px] font-medium hover:opacity-80"
                  style={{ color: GREEN }}
                  data-testid="case-event-edit-btn"
                >
                  <Pencil className="w-3.5 h-3.5" aria-hidden />
                  Edit
                </button>
              }
            >
              <FieldRow icon={Sparkles} label="Title" value={display.eventSummary.title} testId="case-event-title" />
              <FieldRow icon={Calendar} label="Type" value={display.eventSummary.type} testId="case-event-type" />
              <FieldRow icon={Calendar} label="Date" value={display.eventSummary.dateFriendly} testId="case-event-date" />
              <FieldRow icon={MapPin} label="Location" value={display.eventSummary.location} testId="case-event-location" />
              <FieldRow icon={Users} label="Guests" value={display.eventSummary.guests} testId="case-event-guests" />
              <FieldRow icon={DollarSign} label="Budget" value={display.eventSummary.budget} testId="case-event-budget" />
            </RailSection>

            {/* Request — highlighted lavender quote */}
            <RailSection title="Request" testId="case-request-summary">
              {display.requestSummary ? (
                <div
                  className="rounded-xl p-4"
                  style={{ backgroundColor: '#EEE9FE', border: '1px solid #DDD5FA' }}
                  data-testid="case-request-quote"
                >
                  <p
                    className="text-[13.5px] text-gray-800 whitespace-pre-wrap leading-[1.7] italic"
                    style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                  >
                    &ldquo;{display.requestSummary}&rdquo;
                  </p>
                </div>
              ) : (
                <p className="text-[13px] text-gray-400 italic">
                  No written request from the customer.
                </p>
              )}
            </RailSection>
          </aside>

          {/* Center — Brief */}
          <main className="order-1 lg:order-2 min-w-0" data-testid="case-brief-column">
            <ConciergePlaybook eventId={display.eventId} onDraftSaved={loadPublishStatus} />
          </main>

          {/* Right rail — Notes + Activity */}
          <aside className="order-3 space-y-4" data-testid="case-marginalia-rail">
            {/* Notes */}
            <RailSection
              title={`Notes${notes.length > 0 ? ` · ${notes.length}` : ''}`}
              testId="case-notes-section"
              action={
                !formOpen ? (
                  <button
                    type="button"
                    onClick={openForm}
                    className="inline-flex items-center gap-1 text-[12px] font-medium hover:opacity-80"
                    style={{ color: GREEN }}
                    data-testid="case-notes-add-btn"
                  >
                    <PenSquare className="w-3.5 h-3.5" aria-hidden />
                    Add note
                  </button>
                ) : null
              }
            >
              {formOpen && (
                <div className="space-y-2 mb-3" data-testid="case-notes-form">
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Add an internal note visible only to your team."
                    rows={3}
                    maxLength={NOTE_MAX}
                    autoFocus
                    data-testid="case-notes-textarea"
                    className="border-[#E8DEC7] focus-visible:ring-1 focus-visible:ring-[#5B47E8]"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`text-[10px] ${draft.length >= 3800 ? 'text-amber-600' : 'text-gray-400'}`}
                      data-testid="case-notes-form-counter"
                    >
                      {draft.length} / {NOTE_MAX}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={cancelForm}
                        disabled={saving}
                        data-testid="case-notes-form-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveNote}
                        disabled={saving || draft.trim().length === 0 || draft.length > NOTE_MAX}
                        style={{ backgroundColor: PURPLE, color: 'white' }}
                        data-testid="case-notes-form-save"
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {notes.length > 0 ? (
                <ul className="space-y-3" data-testid="case-notes-list">
                  {notes.slice(0, 3).map((n) => {
                    const friendly = relativeTime(n.created_at);
                    return (
                      <li
                        key={n.id}
                        data-testid={`case-note-item-${n.id}`}
                        className="rounded-lg p-3"
                        style={{ backgroundColor: '#FDEFC5', border: '1px solid #F5E4A6' }}
                      >
                        <p
                          className="text-[13px] text-gray-900 whitespace-pre-wrap leading-relaxed"
                          data-testid={`case-note-text-${n.id}`}
                        >
                          {n.text}
                        </p>
                        <p className="mt-1.5 text-[10.5px] text-[#8B5A0F]">
                          <span data-testid={`case-note-author-${n.id}`}>
                            {n.author_display_name
                              || (n.worker_id ? 'Concierge admin' : 'System')}
                          </span>
                          <span aria-hidden> · </span>
                          <span data-testid={`case-note-time-${n.id}`} title={n.created_at || ''}>
                            {friendly || 'just now'}
                          </span>
                        </p>
                      </li>
                    );
                  })}
                  {notes.length > 3 ? (
                    <li className="text-[12px] font-medium" style={{ color: PURPLE }} data-testid="case-notes-view-all">
                      View all {notes.length} notes →
                    </li>
                  ) : null}
                </ul>
              ) : (
                !formOpen && (
                  <div
                    className="rounded-lg p-3 flex items-start gap-2.5"
                    style={{ backgroundColor: '#FDEFC5', border: '1px solid #F5E4A6' }}
                    data-testid="case-notes-empty-state"
                  >
                    <PenSquare className="w-4 h-4 mt-0.5 text-[#8B5A0F] shrink-0" aria-hidden />
                    <div>
                      <p className="text-[13px] font-medium text-gray-900">
                        {notesLoading ? 'Loading notes…' : 'No notes yet.'}
                      </p>
                      <p className="text-[11.5px] text-[#8B5A0F] mt-0.5">
                        Add internal notes visible only to your team.
                      </p>
                    </div>
                  </div>
                )
              )}
            </RailSection>

            {/* Customer responses — current-state decision summary.
                B-EXP-6.19 Defect 4 — this section answers "what has the
                customer currently decided?" rather than replaying the
                chronological history (Activity holds that). Rows are
                grouped by response_type; for a given recommendation only
                the latest response (by updated_at) is shown. Revision
                labels are preserved so older-brief responses are honest
                about their vintage. */}
            <RailSection title="Customer responses" testId="case-responses-section">
              {responses.length === 0 ? (
                <p
                  className="text-[12.5px] text-gray-500 italic"
                  data-testid="case-responses-empty-state"
                >
                  {responsesLoading ? 'Loading responses…' : 'No responses yet. They\u2019ll appear here once the customer opens their brief.'}
                </p>
              ) : (
                <CustomerResponsesSummary responses={responses} />
              )}
            </RailSection>

            {/* Activity — vertical timeline */}
            <RailSection title="Activity" testId="case-history-section">
              {history.length === 0 ? (
                <p
                  className="text-[12.5px] text-gray-500 italic"
                  data-testid="case-history-empty-state"
                >
                  {historyLoading ? 'Loading activity…' : 'Activity will appear here as work happens.'}
                </p>
              ) : (
                <ul
                  className="space-y-4"
                  data-testid="case-history-list"
                >
                  {history.slice(0, 5).map((entry) => (
                    <HistoryRow
                      key={entry.id}
                      entry={entry}
                    />
                  ))}
                  {history.length > 5 ? (
                    <li className="text-[12px] font-medium" style={{ color: PURPLE }} data-testid="case-history-view-all">
                      View full activity →
                    </li>
                  ) : null}
                </ul>
              )}
            </RailSection>

            {/* Case actions — the single home for staff case operations.
                B-EXP-6.21 Part 3 — "Edit customer details" removed from
                this card because the same editor is already reachable
                via Customer card → Edit (data-testid='case-customer-edit-btn').
                Close/Reopen is the only primary action left; do NOT add
                filler actions to populate the card. */}
            <RailSection title="Case actions" testId="case-actions-section">
              <div className="space-y-2" data-testid="case-actions-list">
                {display.isClosed ? (
                  <button
                    type="button"
                    onClick={reopenCase}
                    disabled={reopenSaving}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium bg-[#DFF1E1] text-[#146C43] hover:bg-[#CDE9D2] transition-colors disabled:opacity-70 disabled:cursor-wait"
                    data-testid="case-action-reopen"
                  >
                    <RefreshCw className="w-4 h-4" aria-hidden />
                    {reopenSaving ? 'Reopening…' : 'Reopen case'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCloseModalOpen(true)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium bg-[#FDE4DC] text-[#B4321C] hover:bg-[#FBD5CA] transition-colors"
                    data-testid="case-action-close"
                  >
                    <Flag className="w-4 h-4" aria-hidden />
                    Close case
                  </button>
                )}
              </div>
            </RailSection>
          </aside>
        </div>
      </div>

      {/* Edit modals (B-EXP-3) */}
      <EditCustomerModal
        open={editCustomerOpen}
        onClose={() => setEditCustomerOpen(false)}
        customer={state?.customer}
        onSave={patchCustomer}
      />
      <EditEventModal
        open={editEventOpen}
        onClose={() => setEditEventOpen(false)}
        event={state?.event}
        onSave={patchEvent}
      />
      <CloseCaseModal
        open={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        onConfirm={submitClose}
        saving={closeSaving}
      />
    </div>
  );
}
